/**
 * Supervisor Review Center — Review Work, Not Forms
 * 
 * A unified queue for supervisors to review all pending work:
 * - Work Records (visits needing approval)
 * - Timesheets
 * - Care Notes
 * - Incident Reports
 * 
 * Philosophy: Supervisors review WORK, not hunt across modules.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, XCircle, AlertTriangle, Clock, User, Calendar,
  FileText, ChevronRight, Filter, Search, RefreshCw, Eye,
  ChevronDown, MoreVertical
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { api } from '../../lib/api';

// Status configuration
const STATUS_CONFIG = {
  needs_review: { label: 'Needs Review', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  needs_correction: { label: 'Needs Correction', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  in_review: { label: 'In Review', color: 'bg-blue-100 text-blue-800', icon: Eye },
  exception: { label: 'Has Exceptions', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  ready_for_payroll: { label: 'Ready for Payroll', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  ready_for_billing: { label: 'Ready for Billing', color: 'bg-teal-100 text-teal-800', icon: CheckCircle },
};

export default function ReviewCenter() {
  const navigate = useNavigate();
  const [workRecords, setWorkRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
  });
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filters.status]);

  const fetchData = async () => {
    try {
      const [recordsRes, statsRes] = await Promise.all([
        api.get('/work-records/review-queue', {
          params: filters.status ? { status: filters.status } : {}
        }),
        api.get('/work-records/stats')
      ]);
      setWorkRecords(recordsRes.data.items || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch review data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const handleReviewAction = async (workRecordId, action, notes = null) => {
    try {
      await api.post(`/work-records/${workRecordId}/review`, {
        action,
        notes
      });
      await fetchData();
    } catch (error) {
      console.error('Review action failed:', error);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedRecords.length === 0) return;
    
    try {
      await api.post('/work-records/bulk-review', {
        work_record_ids: selectedRecords,
        action
      });
      setSelectedRecords([]);
      await fetchData();
    } catch (error) {
      console.error('Bulk action failed:', error);
    }
  };

  const toggleRecordSelection = (id) => {
    setSelectedRecords(prev =>
      prev.includes(id)
        ? prev.filter(r => r !== id)
        : [...prev, id]
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatHours = (hours) => {
    if (!hours) return '-';
    return `${hours.toFixed(1)}h`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="review-center">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Supervisor Review Center</h1>
          <p className="text-gray-500 mt-1">Review work, not forms. One unified queue.</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedRecords.length > 0 && (
            <>
              <Badge variant="secondary">{selectedRecords.length} selected</Badge>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleBulkAction('approve')}
                data-testid="bulk-approve-btn"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve All
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <StatCard
            label="Needs Review"
            value={stats.needs_review}
            color="yellow"
            onClick={() => setFilters({ ...filters, status: 'needs_review' })}
          />
          <StatCard
            label="Needs Correction"
            value={stats.needs_correction}
            color="red"
            onClick={() => setFilters({ ...filters, status: 'needs_correction' })}
          />
          <StatCard
            label="With Exceptions"
            value={stats.exception_count}
            color="red"
            onClick={() => navigate('/exceptions')}
          />
          <StatCard
            label="Approved"
            value={stats.approved}
            color="green"
            onClick={() => setFilters({ ...filters, status: 'approved' })}
          />
          <StatCard
            label="Ready for Payroll"
            value={stats.ready_for_payroll}
            color="emerald"
          />
          <StatCard
            label="Today's Work"
            value={stats.today_total}
            color="blue"
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 bg-white p-4 rounded-lg border">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by caregiver or client..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
        </div>
        <Select
          value={filters.status || "all"}
          onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? "" : value })}
        >
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="needs_review">Needs Review</SelectItem>
            <SelectItem value="needs_correction">Needs Correction</SelectItem>
            <SelectItem value="exception">Has Exceptions</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Work Records List */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {workRecords.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">All caught up!</h3>
            <p className="text-gray-500 mt-1">No work records pending review.</p>
          </div>
        ) : (
          <div className="divide-y">
            {workRecords.map((record) => (
              <WorkRecordRow
                key={record.id}
                record={record}
                selected={selectedRecords.includes(record.id)}
                onSelect={() => toggleRecordSelection(record.id)}
                onApprove={() => handleReviewAction(record.id, 'approve')}
                onReturn={() => handleReviewAction(record.id, 'return_for_correction')}
                onView={() => navigate(`/review/${record.id}`)}
                formatDate={formatDate}
                formatHours={formatHours}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, color, onClick }) {
  const colorClasses = {
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    green: 'bg-green-50 border-green-200 text-green-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
  };

  return (
    <button
      onClick={onClick}
      className={`p-4 rounded-lg border ${colorClasses[color]} text-left hover:shadow-sm transition-shadow`}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-sm opacity-80">{label}</p>
    </button>
  );
}

// Work Record Row Component
function WorkRecordRow({
  record,
  selected,
  onSelect,
  onApprove,
  onReturn,
  onView,
  formatDate,
  formatHours
}) {
  const status = STATUS_CONFIG[record.status] || STATUS_CONFIG.needs_review;
  const StatusIcon = status.icon;

  return (
    <div
      className={`p-4 hover:bg-gray-50 transition-colors ${selected ? 'bg-blue-50' : ''}`}
      data-testid={`work-record-${record.id}`}
    >
      <div className="flex items-start gap-4">
        {/* Selection Checkbox */}
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Top Row: Client + Caregiver */}
          <div className="flex items-center gap-3 mb-1">
            <span className="font-medium text-gray-900 truncate">{record.client_name}</span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-600 truncate">
              <User className="inline h-3 w-3 mr-1" />
              {record.caregiver_name}
            </span>
          </div>

          {/* Second Row: Date + Hours + Status */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-gray-500">
              <Calendar className="inline h-3 w-3 mr-1" />
              {formatDate(record.visit_date)}
            </span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-500">
              <Clock className="inline h-3 w-3 mr-1" />
              {formatHours(record.evv?.actual_hours)}
            </span>
            <Badge className={status.color}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {status.label}
            </Badge>
            {record.has_unresolved_exceptions && (
              <Badge className="bg-red-100 text-red-800">
                <AlertTriangle className="h-3 w-3 mr-1" />
                {record.critical_exception_count} Exception{record.critical_exception_count !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={onView}
            data-testid={`view-${record.id}`}
          >
            <Eye className="h-4 w-4" />
          </Button>
          
          {record.status === 'needs_review' && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                onClick={onApprove}
                data-testid={`approve-${record.id}`}
              >
                <CheckCircle className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={onReturn}
                data-testid={`return-${record.id}`}
              >
                <XCircle className="h-4 w-4" />
              </Button>
            </>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onView}>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onApprove}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onReturn}>
                <XCircle className="h-4 w-4 mr-2" />
                Return for Correction
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
